import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {{value: string, optionValueId: string, fieldsetIndex: number} | null} */
  #userClickedOption = null;

  /** @type {boolean} */
  #isAutoFixing = false;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
    
    // Auto-fix invalid selections on page load
    this.#fixInvalidSelectionOnLoad();
  }

  /**
   * Checks if current selections form a valid variant on page load.
   * If not, auto-selects valid options.
   */
  #fixInvalidSelectionOnLoad() {
    // Wait a tick for the DOM to be fully ready
    requestAnimationFrame(() => {
      if (!this.#hasValidVariant()) {
        this.#autoFixSelections();
      }
    });
  }

  /**
   * Checks if current selections result in a valid variant.
   * @returns {boolean}
   */
  #hasValidVariant() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    for (const fieldset of fieldsets) {
      const checkedInput = fieldset.querySelector('input:checked');
      if (checkedInput instanceof HTMLInputElement && checkedInput.dataset.variantId) {
        return true;
      }
    }
    return false;
  }

  /**
   * Auto-fixes selections to create a valid variant.
   * Prioritizes keeping the first fieldset's selection.
   */
  #autoFixSelections() {
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    if (fieldsets.length < 2) return;

    // For each fieldset, if current selection is invalid, find a valid one
    for (const fieldset of fieldsets) {
      if (!fieldset) continue;
      const checkedInput = fieldset.querySelector('input:checked');
      const inputs = Array.from(fieldset.querySelectorAll('input'));
      
      // If nothing checked or checked has no variant ID, find one that does
      if (!checkedInput || (checkedInput instanceof HTMLInputElement && !checkedInput.dataset.variantId)) {
        const validInput = inputs.find(input => 
          input instanceof HTMLInputElement && input.dataset.variantId
        );
        
        if (validInput && validInput instanceof HTMLInputElement) {
          inputs.forEach(input => {
            if (input instanceof HTMLInputElement) input.checked = false;
          });
          validInput.checked = true;
        }
      }
    }
    
    // Trigger a change to update everything
    const checkedInput = this.querySelector('fieldset input:checked');
    if (checkedInput instanceof HTMLInputElement) {
      this.#isAutoFixing = true;
      checkedInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const clickedValue = event.target.getAttribute('value') || '';
    const optionValueId = event.target.dataset.optionValueId || '';
    const fieldsets = Array.from(this.querySelectorAll('fieldset'));
    const clickedFieldset = event.target.closest('fieldset');
    const fieldsetIndex = clickedFieldset ? fieldsets.indexOf(clickedFieldset) : -1;

    // Track what the user clicked (unless we're auto-fixing)
    if (!this.#isAutoFixing) {
      this.#userClickedOption = {
        value: clickedValue,
        optionValueId: optionValueId,
        fieldsetIndex: fieldsetIndex
      };
      console.log('[VariantPicker] User clicked:', this.#userClickedOption);
    } else {
      console.log('[VariantPicker] Auto-fix change:', clickedValue);
    }

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: event.target.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      Theme.template.name === 'product' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = event.target.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(event.target), loadsNewProduct);

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (event.target.dataset.variantId) {
        url.searchParams.set('variant', event.target.dataset.variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      history.replaceState({}, '', url.toString());
    }

    // Only update dimensions if we have a valid variant
    if (event.target.dataset.variantId) {
      this.updateVariantDimensions(event.target.dataset.variantId);
    }
  }

  /**
   * Updates variant dimensions display based on the selected variant
   * @param {string | undefined} variantId - The variant ID
   */
  updateVariantDimensions(variantId) {
    if (!variantId) return;
    
    const validVariantId = /** @type {string} */ (variantId);
    const variantMetafieldData = document.querySelector('#variant_metafield_data');
    
    if (!variantMetafieldData || !variantMetafieldData.textContent) return;
    
    const variantMetafieldDataJson = JSON.parse(variantMetafieldData.textContent);
    const variantMetafield = variantMetafieldDataJson[validVariantId];
    
    if (!variantMetafield) return;
    
    const dimensionsTextElement = document.querySelector('.dimension-imperial');
    if (!dimensionsTextElement) return;
    
    let dimensionsValue = '';
    let metricDimension = '';
    
    if (variantMetafield.mirror_diameter) {
      if (variantMetafield.mirror_diameter < 2) {
        const diameterValue = parseFloat(variantMetafield.mirror_diameter);
        dimensionsValue = `${variantMetafield.mirror_diameter}" Dia.`;
        const metricValue = (diameterValue * 25.4).toFixed(3);
        metricDimension = `(${metricValue}mm Dia.)`;
      } else {
        const diameterValue = parseFloat(variantMetafield.mirror_diameter);
        dimensionsValue = `${diameterValue}mm Dia.`;
        const metricValue = (diameterValue / 25.4).toFixed(3);
        metricDimension = `(${metricValue}" Dia.)`;
      }
    } else if (variantMetafield.mirror_area) {
      const areaValue = parseFloat(variantMetafield.mirror_area);
      dimensionsValue = `${variantMetafield.mirror_area}" Sq.`;
      const metricValue = (areaValue * 6.4516).toFixed(3);
      metricDimension = `(${metricValue}cm²)`;
    } else if (variantMetafield.mirror_length && variantMetafield.mirror_width) {
      const lengthValue = parseFloat(variantMetafield.mirror_length);
      const widthValue = parseFloat(variantMetafield.mirror_width);
      dimensionsValue = `${variantMetafield.mirror_length}" L x ${variantMetafield.mirror_width}" W`;
      const metricLength = (lengthValue * 25.4).toFixed(3);
      const metricWidth = (widthValue * 25.4).toFixed(3);
      metricDimension = `(${metricLength}mm x ${metricWidth}mm)`;
    }
    
    if (!dimensionsValue) return;
    
    dimensionsTextElement.textContent = dimensionsValue;
    
    const metricDimensionElement = document.querySelector('.dimension-metric');
    if (metricDimensionElement && metricDimension) {
      metricDimensionElement.textContent = metricDimension;
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);
      if (!targetElement) throw new Error('Target element not found');
      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);
      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }
      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    console.log('[VariantPicker] Fetching:', requestUrl);

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined}
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    // Before morphing: ensure user's clicked option is selected in new HTML
    // and fix other fieldsets to have valid options
    const needsSecondFetch = this.#prepareNewHtmlSelections(newVariantPickerSource);

    morph(this, newVariantPickerSource);

    // After morphing: if we made changes that require a second fetch to get
    // proper validity data, do it now
    if (needsSecondFetch && !this.#isAutoFixing) {
      console.log('[VariantPicker] Triggering second fetch to get valid options');
      this.#isAutoFixing = true;
      
      // Find the input for the user's clicked option and trigger change
      const checkedInput = this.querySelector('fieldset input:checked');
      if (checkedInput instanceof HTMLElement) {
        this.fetchUpdatedSection(this.buildRequestUrl(checkedInput), false);
      }
    } else if (this.#isAutoFixing) {
      // After the auto-fix fetch returns, update dimensions
      this.#isAutoFixing = false;
      this.#userClickedOption = null;
      
      // Find the valid variant and update dimensions
      const validInput = this.querySelector('fieldset input:checked[data-variant-id]');
      if (validInput instanceof HTMLInputElement && validInput.dataset.variantId) {
        console.log('[VariantPicker] Auto-fix complete, updating dimensions');
        this.updateVariantDimensions(validInput.dataset.variantId);
        
        // Also update the URL
        const url = new URL(window.location.href);
        url.searchParams.set('variant', validInput.dataset.variantId);
        history.replaceState({}, '', url.toString());
      }
    }

    return newProduct;
  }

  /**
   * Prepares selections in the new HTML before morphing.
   * Ensures user's clicked option stays selected and other fieldsets have valid options.
   * @param {Element} newPickerSource
   * @returns {boolean} True if a second fetch is needed
   */
  #prepareNewHtmlSelections(newPickerSource) {
    const fieldsets = Array.from(newPickerSource.querySelectorAll('fieldset'));
    const userClicked = this.#userClickedOption;
    let needsSecondFetch = false;

    console.log('[VariantPicker] Preparing new HTML, userClicked:', userClicked);

    for (let i = 0; i < fieldsets.length; i++) {
      const fieldset = fieldsets[i];
      if (!fieldset) continue;

      const legend = fieldset.querySelector('legend')?.textContent?.trim() || `Fieldset ${i}`;
      const inputs = Array.from(fieldset.querySelectorAll('input'));
      const checkedInput = fieldset.querySelector('input:checked');

      // Log current state
      console.log(`[VariantPicker] ${legend}:`, {
        checkedValue: checkedInput instanceof HTMLInputElement ? checkedInput.value : 'none',
        options: inputs.map(inp => ({
          value: inp instanceof HTMLInputElement ? inp.value : '',
          hasVariantId: inp instanceof HTMLInputElement ? !!inp.dataset.variantId : false
        }))
      });

      // If this is the fieldset the user clicked in, ensure their selection is checked
      if (userClicked && i === userClicked.fieldsetIndex) {
        const userInput = inputs.find(inp => 
          inp instanceof HTMLInputElement && inp.value === userClicked.value
        );
        
        if (userInput && userInput instanceof HTMLInputElement) {
          // Uncheck all, check user's choice
          inputs.forEach(inp => {
            if (inp instanceof HTMLInputElement) {
              inp.checked = false;
              inp.removeAttribute('checked');
            }
          });
          userInput.checked = true;
          userInput.setAttribute('checked', 'checked');
          console.log(`[VariantPicker] Preserved user selection: ${userClicked.value}`);
          
          // If user's selection doesn't have a variant ID, we'll need a second fetch
          if (!userInput.dataset.variantId) {
            needsSecondFetch = true;
          }
        }
      } else {
        // For other fieldsets, select a valid option if nothing valid is checked
        const currentChecked = fieldset.querySelector('input:checked');
        const hasValidSelection = currentChecked instanceof HTMLInputElement && currentChecked.dataset.variantId;

        if (!hasValidSelection) {
          // Find an option with a variant ID
          const validInput = inputs.find(inp => 
            inp instanceof HTMLInputElement && inp.dataset.variantId
          );

          if (validInput && validInput instanceof HTMLInputElement) {
            inputs.forEach(inp => {
              if (inp instanceof HTMLInputElement) {
                inp.checked = false;
                inp.removeAttribute('checked');
              }
            });
            validInput.checked = true;
            validInput.setAttribute('checked', 'checked');
            console.log(`[VariantPicker] Auto-selected valid option: ${validInput.value}`);
          } else if (!currentChecked) {
            // Nothing checked and no valid options - select first option
            const firstInput = inputs[0];
            if (firstInput && firstInput instanceof HTMLInputElement) {
              firstInput.checked = true;
              firstInput.setAttribute('checked', 'checked');
              needsSecondFetch = true;
              console.log(`[VariantPicker] Selected first option as fallback: ${firstInput.value}`);
            }
          }
        }
      }
    }

    return needsSecondFetch;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined}
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined}
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]}
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
